import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type ReactElement, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ScreenFailure } from '../channel-selection/screen-failure';

import { FailureAlert } from './FailureAlert';

/**
 * How long the box and "Authorize and continue" stay disabled after the consent block mounts
 * (specs/S6-trading-account/spec.md R34 (c), E18 of runbook 04; Proposal, to be validated). A press
 * or a key meant for something else, landing on the block just as it appears under the form, then
 * does nothing. The block is remounted by its `key` on every new answer of the server (R13), so
 * every new appearance is armed afresh.
 */
export const CONSENT_ARMING_MS = 1500;

/** Props of {@link ConsentBlock}. */
export interface ConsentBlockProps {
  /** Last 4 digits of the account, the only part of its number the screen shows (R13, R18, Q81). */
  readonly last4: string;
  /** True while `POST /consent` is in flight: the box and the button are disabled (§4 "Authorizing"). */
  readonly busy: boolean;
  /** The refusal of the last consent, shown under the button (R29). */
  readonly failure: ScreenFailure | null;
  /** The trader pressed "Authorize and continue" with the box checked. */
  readonly onAuthorize: () => void;
}

/**
 * What FXMate can and can never do, the consent checkbox and "Authorize and continue"
 * (specs/S6-trading-account/spec.md R12, R13, I7).
 *
 * **I7.** The box is local state that starts unchecked on every mount: the screen gives this
 * component a `key` that changes with every new answer of the server and every 422, so a new
 * verification or a refused text always brings it back unchecked. It is separate from the form and
 * nothing is sent before the press. The button is disabled until the box is checked.
 *
 * **R34 (E18; Proposal, to be validated).** Only the box ticks it: the label text is not a click
 * target (the box takes its accessible name from it by `aria-labelledby`). For
 * {@link CONSENT_ARMING_MS} after each mount the box and the button are disabled.
 */
export function ConsentBlock({
  last4,
  busy,
  failure,
  onAuthorize,
}: ConsentBlockProps): ReactElement {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [armed, setArmed] = useState(false);
  const labelId = useId();
  useEffect(() => {
    const timer = setTimeout(() => {
      setArmed(true);
    }, CONSENT_ARMING_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 1.75,
          borderRadius: 3.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
          {t('onboarding.tradingAccount.can')}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
          {t('onboarding.tradingAccount.never')}
        </Typography>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'flex-start',
            pt: 1.25,
            gap: 1,
            borderTop: `1px solid ${theme.fx.border}`,
          })}
        >
          <Checkbox
            checked={checked}
            disabled={busy || !armed}
            onChange={(event) => {
              setChecked(event.target.checked);
            }}
            slotProps={{ input: { 'aria-labelledby': labelId } }}
            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 28 } }}
          />
          <Typography id={labelId}>
            {t('onboarding.tradingAccount.consent.label', { last4 })}
          </Typography>
        </Box>
      </Paper>
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!armed || !checked || busy}
        onClick={onAuthorize}
        sx={{ minHeight: 52 }}
      >
        {t('onboarding.tradingAccount.consent.submit')}
      </Button>
      {failure === null ? null : <FailureAlert failure={failure} />}
    </Box>
  );
}
